import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Loader2, ScanSearch } from "lucide-react";
import { fmtDT } from "./ActivityTab";

const SEV_STYLE = { CRITICAL: "bg-red-600 text-white", HIGH: "bg-red-100 text-red-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-slate-100 text-slate-600", UNKNOWN: "bg-slate-100 text-slate-500" };
const FILTERS = ["SERIOUS", "ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW", "UNKNOWN"];

export function CodeScanTab() {
  const [scans, setScans] = useState([]);
  const [scan, setScan] = useState(null);
  const [filter, setFilter] = useState("SERIOUS");
  const [directOnly, setDirectOnly] = useState(false);
  const running = scans[0]?.status === "running";

  const load = useCallback(async () => {
    const r = await apiClient.get("/security-monitor/scans");
    setScans(r.data);
    const latest = r.data.find((s) => s.status === "completed");
    if (latest) setScan((await apiClient.get(`/security-monitor/scans/${latest.id}`)).data);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Could not load scans")); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async () => {
    try { await apiClient.post("/security-monitor/scans"); toast.success("Scan started — this takes about a minute"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start scan"); }
  };

  const findings = useMemo(() => {
    const all = (scan?.findings || []).filter((f) => !directOnly || f.direct !== false);
    if (filter === "ALL") return all;
    if (filter === "SERIOUS") return all.filter((f) => f.severity === "CRITICAL" || f.severity === "HIGH");
    return all.filter((f) => f.severity === filter);
  }, [scan, filter, directOnly]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="text-base">Third-party code scan</CardTitle>
            <CardDescription>Runs every Monday at 06:00 UTC against the public OSV vulnerability database. Superusers are emailed about critical and high findings.</CardDescription>
          </div>
          <Button onClick={start} disabled={running} className="rounded-sm gap-2" data-testid="run-code-scan-btn">
            {running ? <Loader2 size={15} className="animate-spin" /> : <ScanSearch size={15} />}{running ? "Scanning…" : "Run scan now"}
          </Button>
        </CardHeader>
        <CardContent>
          {scan ? (
            <div className="flex flex-wrap gap-3 items-center text-sm" data-testid="code-scan-summary">
              <span className="text-slate-500">Last scan {fmtDT(scan.completed_at)} · {scan.packages_scanned.python} Python + {scan.packages_scanned.npm} JavaScript packages</span>
              {Object.entries(scan.counts).map(([k, v]) => <Badge key={k} className={`rounded-sm text-xs ${SEV_STYLE[k]}`} data-testid={`scan-count-${k.toLowerCase()}`}>{v} {k.toLowerCase()}</Badge>)}
            </div>
          ) : <p className="text-sm text-slate-500" data-testid="code-scan-empty">No completed scan yet.</p>}
          {scans[0]?.status === "failed" && <p className="text-sm text-red-600 mt-2">The last scan failed: {scans[0].error}</p>}
        </CardContent>
      </Card>
      {scan && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Findings ({findings.length})</CardTitle>
            <div className="flex gap-1 flex-wrap items-center">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 mr-2">
                <Switch checked={directOnly} onCheckedChange={setDirectOnly} data-testid="scan-direct-only-switch" /> Hide indirect JavaScript packages
              </label>
              {FILTERS.map((f) => (
                <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className="h-7 rounded-sm text-xs" onClick={() => setFilter(f)} data-testid={`scan-filter-${f.toLowerCase()}`}>
                  {f === "SERIOUS" ? "Critical + high" : f.charAt(0) + f.slice(1).toLowerCase()}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="code-scan-findings">
              <thead><tr className="border-b text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">Package</th><th className="py-2 pr-3">Version</th><th className="py-2 pr-3">Severity</th><th className="py-2 pr-3">Issue</th><th className="py-2">Fixed in</th>
              </tr></thead>
              <tbody>
                {findings.slice(0, 300).map((f) => (
                  <tr key={`${f.ecosystem}-${f.package}-${f.version}-${f.vuln_id}`} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3"><p className="font-medium">{f.package}</p><p className="text-[11px] text-slate-400">{f.ecosystem === "PyPI" ? "Python (backend)" : `JavaScript (frontend${f.direct === false ? ", indirect" : ", direct"})`}</p></td>
                    <td className="py-2 pr-3 font-mono text-xs">{f.version}</td>
                    <td className="py-2 pr-3"><Badge className={`rounded-sm text-xs ${SEV_STYLE[f.severity]}`}>{f.severity.toLowerCase()}</Badge></td>
                    <td className="py-2 pr-3 max-w-md"><a href={`https://osv.dev/vulnerability/${f.vuln_id}`} target="_blank" rel="noreferrer" className="text-cyan-700 hover:underline text-xs">{f.vuln_id}{f.aliases?.length ? ` (${f.aliases.join(", ")})` : ""}</a><p className="text-xs text-slate-600 mt-0.5">{f.summary}</p></td>
                    <td className="py-2 font-mono text-xs">{f.fixed_in || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {findings.length > 300 && <p className="text-xs text-slate-500 mt-2">Showing the first 300.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
